import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { milestoneApi, api } from '../../api/client';
import type { MilestoneContribution, CarouselEvent } from '../../types';
import { format } from 'date-fns';

export function MilestoneContributionsReview() {
  const { id } = useParams<{ id: string }>();
  const [contributions, setContributions] = useState<MilestoneContribution[]>([]);
  const [existingEvents, setExistingEvents] = useState<CarouselEvent[]>([]);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [destinations, setDestinations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      milestoneApi.listContributions(id),
      api.get('/events').then(({ data }) => data.events ?? []),
    ]).then(([contribs, events]) => {
      setContributions(contribs);
      setExistingEvents(events);
      const initialDates: Record<string, string> = {};
      for (const c of contribs) {
        initialDates[c.id] = c.suggestedDate ? c.suggestedDate.slice(0, 10) : '';
      }
      setDates(initialDates);
    }).finally(() => setLoading(false));
  }, [id]);

  async function handleApprove(contributionId: string) {
    if (!id) return;
    setBusyId(contributionId);
    try {
      await milestoneApi.approveContribution(id, contributionId, {
        takenAt: dates[contributionId] || undefined,
        eventId: destinations[contributionId] || undefined,
      });
      setContributions(prev => prev.filter(c => c.id !== contributionId));
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(contributionId: string) {
    if (!id) return;
    setBusyId(contributionId);
    try {
      await milestoneApi.rejectContribution(id, contributionId);
      setContributions(prev => prev.filter(c => c.id !== contributionId));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="max-w-2xl"><p className="text-slate-500">Loading…</p></div>;

  return (
    <div className="max-w-2xl">
      <Link to="/events/milestones" className="text-sm text-indigo-600 hover:underline mb-2 inline-block">← My Milestones</Link>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Review Contributions</h1>
      <p className="text-slate-500 text-sm mb-6">Photos submitted via your shared link — nothing joins your archive until you approve it.</p>

      {contributions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-indigo-200">
          <p className="text-slate-500 font-medium">Nothing waiting for review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contributions.map(c => (
            <div key={c.id} data-testid="contribution-card" className="bg-white rounded-2xl border border-indigo-50 shadow-sm p-4 flex flex-col sm:flex-row gap-4">
              <img src={c.filePath} alt="" loading="lazy" className="w-28 h-28 rounded-xl object-cover flex-shrink-0 bg-slate-100" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  Submitted by {c.contributorName?.trim() || 'Anonymous'}
                </p>
                {c.message && <p className="text-sm text-slate-500 italic mt-0.5">"{c.message}"</p>}
                <p className="text-xs text-slate-400 mt-0.5">{format(new Date(c.createdAt), 'MMM d, yyyy')}</p>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">When was this taken?</label>
                    <input
                      type="date"
                      data-testid={`contribution-date-${c.id}`}
                      value={dates[c.id] ?? ''}
                      onChange={e => setDates(prev => ({ ...prev, [c.id]: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {!dates[c.id] && (
                      <p className="text-[0.65rem] text-amber-600 mt-1">No date detected — set one or this photo won't appear in the slideshow.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Attach to</label>
                    <select
                      value={destinations[c.id] ?? ''}
                      onChange={e => setDestinations(prev => ({ ...prev, [c.id]: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Contributed Photos (default)</option>
                      {existingEvents.map(e => (
                        <option key={e.id} value={e.id}>{e.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={busyId === c.id}
                    onClick={() => handleApprove(c.id)}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={busyId === c.id}
                    onClick={() => handleReject(c.id)}
                    className="px-3 py-1.5 bg-white text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-100 border border-slate-200 disabled:opacity-50 transition-colors"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
